/**
 * Which Procedure Template the Form Builder's template selector opens on.
 *
 * Split out of `useCrossFormAuthoring` so the rule is testable on its own —
 * vitest cannot run `.tsx`, and this is the part with real behaviour in it.
 *
 * The selector was built by z8nrz7cnhk, which made versions distinguishable
 * (the `(vN)` suffix, the searchable list) but gave the control no default, so
 * every edit opened on `Template: None` and the author re-picked a version they
 * had already picked. This supplies the default.
 *
 * Two rules the whole module is shaped around:
 *
 * - **A stored choice always wins.** z8nrz7ckmy is an open urgent defect caused
 *   by a version being swapped out from under a form, so nothing here overrides
 *   an association that already exists — not even when a newer ACTIVE version
 *   has appeared since.
 * - **The default is display-only.** Returning an id is not choosing one:
 *   nothing is persisted until the author opens the list and picks, which is
 *   what keeps opening a form from minting a version or an audit entry.
 */

/** One Procedure Template containing the form being edited. */
export interface ContainingTemplate {
  id: string
  name: string
  /** Version number within `familyId`. Null for unversioned/legacy rows. */
  version: number | null
  /** False once the version is retired — INACTIVE or superseded. */
  active: boolean
  /** Groups the versions of one template. Null = a family of one. */
  familyId: string | null
  /** ISO timestamp of the template's last edit. Breaks ties across families. */
  updatedAt: string
}

/**
 * The sentinel the form row stores for an explicit `Template: None`.
 *
 * The column is a single nullable string, so `null` cannot mean BOTH "the
 * author chose None" and "nobody has chosen yet" — the explicit choice would
 * undo itself on the next open. A sentinel keeps the two apart without a
 * migration.
 */
export const TEMPLATE_NONE = 'none'

/**
 * The versions offered in the dropdown: ACTIVE only.
 *
 * Criterion D of 86eybvqgn — "inactive form versions are excluded from the
 * sequence editor's form picker and from all 'latest version' offers" — and
 * this is a latest-version offer. A retired version stays visible when a form
 * is already pinned to it (see `pinnedInactiveTemplate`), but it is never
 * something a user can newly pick here.
 */
export function selectableTemplates(all: ContainingTemplate[]): ContainingTemplate[] {
  return all.filter((t) => t.active)
}

/**
 * The version the selector opens on when the form has no stored association:
 * the latest ACTIVE template the form is actually used in.
 *
 * Two orderings, because two different questions are being asked:
 *
 * 1. WITHIN a family, by version NUMBER — never by created or updated date.
 *    Given ACTIVE v9, v10 and v11, v11 wins even if v9 was edited most
 *    recently. That is the "latest version" rule of 86eybvqgn criterion D.
 * 2. ACROSS families, by `updatedAt`. A form is commonly used in several
 *    unrelated templates at once — Patient Chart sits in three here — and
 *    version numbers say nothing about "Collection v1 vs KIT Template v3".
 *    The most recently touched template is the one the author is working in.
 *
 * An earlier cut returned null whenever the form was in more than one family,
 * on the reasoning that picking between them was a guess. In this data that
 * was almost every form, so the selector stayed on None and the default never
 * appeared. Defaulting to the most recent beats defaulting to nothing: it is
 * display-only, and one click changes it.
 *
 * Returns null — `Template: None` — only when there is no ACTIVE template at
 * all, which covers both a fully retired family and a form in no template.
 */
export function defaultTemplateId(all: ContainingTemplate[]): string | null {
  const active = selectableTemplates(all)
  if (active.length === 0) return null
  // Each family reduced to its own latest version FIRST, so a family never
  // wins on the strength of an older version that happens to be newly edited.
  const leaders = new Map<string, ContainingTemplate>()
  for (const t of active) {
    // No familyId means no lineage to be the latest of — such a template is a
    // family of one, keyed by its own id.
    const key = t.familyId ?? t.id
    const best = leaders.get(key)
    if (!best || (t.version ?? 0) > (best.version ?? 0)) leaders.set(key, t)
  }
  return [...leaders.values()].reduce((best, t) =>
    // Ties broken by id, so the same form always opens on the same template
    // rather than on whatever order the API happened to return.
    t.updatedAt > best.updatedAt || (t.updatedAt === best.updatedAt && t.id > best.id) ? t : best,
  ).id
}

/**
 * The selector's current value: the stored choice when there is one, otherwise
 * the default. Null renders as `Template: None`.
 *
 * `stored` is the raw column: a template id, the `none` sentinel, or null for
 * "never chosen". Only the last of those reaches the default.
 *
 * A stored id that names no containing template — the template was deleted, or
 * the form was moved out of it — falls through to the default rather than
 * holding a selection that resolves to nothing.
 */
export function resolveTemplateId(
  stored: string | null | undefined,
  all: ContainingTemplate[],
): string | null {
  if (stored === TEMPLATE_NONE) return null
  if (stored && all.some((t) => t.id === stored)) return stored
  return defaultTemplateId(all)
}

/**
 * The stored template when it is one the dropdown no longer offers, so the
 * trigger can still show what the form is pinned to, `(vN)` and all.
 *
 * Display only: it deliberately does NOT go back into the list of options. The
 * author needs to SEE that the form is on a retired version; letting anyone
 * newly pick one is the thing criterion D rules out.
 */
export function pinnedInactiveTemplate(
  selectedId: string | null,
  all: ContainingTemplate[],
): ContainingTemplate | null {
  if (!selectedId) return null
  const t = all.find((x) => x.id === selectedId)
  return t && !t.active ? t : null
}

/** `Auto HP GS (v12)`, or just the name when the template carries no version. */
export function templateLabel(t: { name: string; version: number | null }): string {
  return t.version != null ? `${t.name} (v${t.version})` : t.name
}
